// frontend/src/pages/reports/ReportPage.js
import { useState, useRef } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
    PieChart, Pie, Cell,
} from 'recharts';
import {
    BarChart3, FileText, CheckCircle2, Coins, Percent,
    RefreshCw, Download, Printer, ArrowUpRight, ArrowDownRight,
    Activity, Calendar, LayoutGrid,
} from 'lucide-react';
import useCountUp from '../../hooks/useCountUp';

// =========================================================
// CONSTANTS
// =========================================================
const PERIODS = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'quarter', label: '3M' },
    { value: 'year', label: 'Year' },
];

const CHART_COLORS = ['#3a663f', '#4d8c54', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// =========================================================
// MAIN
// =========================================================
export default function ReportPage() {
    const [period, setPeriod] = useState('month');
    const [reportType, setReportType] = useState('overview');
    const printRef = useRef(null);

    const statsQuery = useQuery(
        ['report-stats', period],
        () => api.get(`/reports/stats?period=${period}`).then((r) => r.data),
        {
            refetchOnWindowFocus: true,
            staleTime: 15_000,
            retry: 1,
            refetchInterval: 30_000,
            refetchIntervalInBackground: false,
        }
    );

    const chartsQuery = useQuery(
        ['report-charts', period],
        () => api.get(`/reports/charts?period=${period}`).then((r) => r.data),
        {
            refetchOnWindowFocus: true,
            staleTime: 15_000,
            retry: 1,
            refetchInterval: 60_000,
            refetchIntervalInBackground: false,
        }
    );

    const handleRefresh = () => {
        statsQuery.refetch();
        chartsQuery.refetch();
    };

    // ---------- Loading ----------
    if (statsQuery.isLoading || chartsQuery.isLoading) return <SkeletonLoader />;

    // ---------- Error ----------
    if (statsQuery.error || chartsQuery.error) {
        const err = statsQuery.error || chartsQuery.error;
        return (
            <div className="max-w-3xl mx-auto px-4 py-20 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 dark:bg-red-900/20 mb-4">
                    <Activity className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Failed to Load Reports</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
                    {err?.message || 'An error occurred'}
                </p>
                <button
                    onClick={handleRefresh}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 bg-brand-600 text-white rounded-xl hover:bg-brand-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/50 focus:ring-offset-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Try Again
                </button>
            </div>
        );
    }

    // ---------- Extract ----------
    const stats = statsQuery.data?.data || statsQuery.data || {};
    const chartData = chartsQuery.data?.data || chartsQuery.data || [];

    const totalQuotes = stats.totalQuotes || 0;
    const activePolicies = stats.activePolicies || 0;
    const totalCommissions = stats.totalCommissions || stats.totalPremium || 0;
    const conversionRate = stats.conversionRate || 0;
    const growthRate = stats.growthRate || 0;
    const policyGrowth = stats.policyGrowth || 0;
    const avgPremium = stats.avgPremium || 0;
    const avgQuoteValue = stats.avgQuoteValue || 0;
    const pendingQuotes = stats.pendingQuotes || 0;
    const recentTransactions = stats.recentTransactions || [];
    const trendQuotes = stats.trend?.quotes || [];
    const trendPremium = stats.trend?.premium || [];

    const safeChartData = Array.isArray(chartData) ? chartData : [];
    const hasChartData = safeChartData.length > 0;

    const isFetching = statsQuery.isFetching || chartsQuery.isFetching;

    return (
        <div ref={printRef} className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-fadeIn">

            {/* ============ HERO ============ */}
            <div className="relative bg-gradient-to-br from-[#1a3327] via-[#2d5232] to-[#3a663f] rounded-3xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full translate-y-1/3 -translate-x-1/4 pointer-events-none" />

                <div className="relative flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                    <div className="space-y-3 max-w-2xl">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-green-300" />
                            <p className="text-green-200 text-xs uppercase tracking-widest font-semibold">
                                Performance Analytics
                            </p>
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight">
                            Reports &amp; Insights
                        </h1>
                        <p className="text-green-100/80">
                            Track performance, analyze trends, and make data-driven decisions.
                        </p>

                        <div className="flex flex-wrap gap-2 pt-1">
                            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs border border-white/10">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                Auto-refresh
                            </span>
                            <span className="inline-flex items-center gap-2 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs border border-white/10">
                                <Calendar className="w-3 h-3" />
                                {stats.periodLabel || 'This Month'}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                        <div className="flex bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-1 w-full sm:w-auto">
                            {PERIODS.map((p) => (
                                <button
                                    key={p.value}
                                    onClick={() => setPeriod(p.value)}
                                    className={`flex-1 sm:flex-initial px-4 py-2 text-xs font-semibold rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-white/40 ${period === p.value
                                            ? 'bg-white text-[#1a3327] shadow-sm'
                                            : 'text-white/80 hover:bg-white/10'
                                        }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={handleRefresh}
                            disabled={isFetching}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/20 text-white px-4 py-2.5 rounded-xl transition-all flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-white/40 disabled:opacity-60"
                        >
                            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* ============ KPI CARDS ============ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <KpiCard
                    icon={<FileText className="w-6 h-6" />}
                    label="Total Quotes"
                    value={totalQuotes}
                    change={growthRate}
                    subtext={`${pendingQuotes} pending`}
                    sparkline={trendQuotes}
                    gradient="from-blue-500 to-blue-600"
                />
                <KpiCard
                    icon={<CheckCircle2 className="w-6 h-6" />}
                    label="Active Policies"
                    value={activePolicies}
                    change={policyGrowth}
                    subtext={`${conversionRate}% conversion`}
                    gradient="from-emerald-500 to-emerald-600"
                />
                <KpiCard
                    icon={<Coins className="w-6 h-6" />}
                    label="Commissions"
                    value={totalCommissions}
                    prefix="KES "
                    change={policyGrowth}
                    subtext={`Avg KES ${Math.round(avgPremium).toLocaleString()}`}
                    sparkline={trendPremium}
                    gradient="from-amber-500 to-amber-600"
                />
                <KpiCard
                    icon={<Percent className="w-6 h-6" />}
                    label="Conversion"
                    value={conversionRate}
                    suffix="%"
                    change={growthRate}
                    subtext={`Avg quote KES ${Math.round(avgQuoteValue).toLocaleString()}`}
                    gradient="from-rose-500 to-rose-600"
                />
            </div>

            {/* ============ CHARTS ============ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-700 p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                                <span className="w-1.5 h-6 bg-brand-600 rounded-full" />
                                Performance Trends
                            </h3>
                            <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                                Monthly overview of quotes, policies, and commissions
                            </p>
                        </div>
                        <div className="flex gap-1 bg-gray-100 dark:bg-slate-700 rounded-lg p-1">
                            {['overview', 'detailed'].map((t) => (
                                <button
                                    key={t}
                                    onClick={() => setReportType(t)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-brand-500/40 ${reportType === t
                                            ? 'bg-white dark:bg-slate-600 text-brand-600 dark:text-green-400 shadow-sm'
                                            : 'text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-white'
                                        }`}
                                >
                                    {t === 'overview' ? 'Overview' : 'Detailed'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {!hasChartData ? (
                        <EmptyChart message="No chart data available for this period." />
                    ) : (
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={safeChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                    <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
                                    <Tooltip
                                        contentStyle={{
                                            borderRadius: '12px',
                                            border: 'none',
                                            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                                            backgroundColor: 'white',
                                            padding: '10px 14px',
                                            fontSize: '12px',
                                        }}
                                        cursor={{ fill: 'rgba(58,102,63,0.06)' }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '12px' }} />
                                    <Bar dataKey="quotes" name="Quotes" fill={CHART_COLORS[0]} radius={[6, 6, 0, 0]} maxBarSize={40} />
                                    {reportType === 'detailed' && (
                                        <Bar dataKey="policies" name="Policies" fill={CHART_COLORS[1]} radius={[6, 6, 0, 0]} maxBarSize={40} />
                                    )}
                                    <Bar dataKey="commissions" name="Commissions (KES K)" fill={CHART_COLORS[2]} radius={[6, 6, 0, 0]} maxBarSize={40} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-700 p-6">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2 mb-1">
                        <span className="w-1.5 h-6 bg-purple-500 rounded-full" />
                        Distribution
                    </h3>
                    <p className="text-xs text-gray-400 dark:text-slate-500 mb-5">
                        Breakdown by activity type
                    </p>

                    {totalQuotes === 0 && activePolicies === 0 ? (
                        <EmptyChart message="No data to display yet." compact />
                    ) : (
                        <>
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={[
                                                { name: 'Quotes', value: totalQuotes || 1 },
                                                { name: 'Policies', value: activePolicies || 1 },
                                            ]}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={50}
                                            outerRadius={80}
                                            paddingAngle={3}
                                            labelLine={false}
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                            dataKey="value"
                                        >
                                            <Cell fill={CHART_COLORS[0]} />
                                            <Cell fill={CHART_COLORS[1]} />
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                borderRadius: '12px',
                                                border: 'none',
                                                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                                                fontSize: '12px',
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="flex justify-center gap-5 mt-4">
                                <LegendDot color={CHART_COLORS[0]} label={`${totalQuotes} Quotes`} />
                                <LegendDot color={CHART_COLORS[1]} label={`${activePolicies} Policies`} />
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ============ RECENT TRANSACTIONS ============ */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-100 dark:border-slate-700 p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                            <span className="w-1.5 h-6 bg-rose-500 rounded-full" />
                            Recent Transactions
                        </h3>
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                            Latest activities across your portfolio
                        </p>
                    </div>
                    <Link
                        to="/quotes"
                        className="text-sm text-brand-600 dark:text-green-400 hover:underline font-semibold flex items-center gap-1"
                    >
                        View All
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                {recentTransactions.length > 0 ? (
                    <div className="overflow-x-auto -mx-6 px-6">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-slate-700">
                                    <Th>Reference</Th>
                                    <Th>Type</Th>
                                    <Th>Amount</Th>
                                    <Th>Date</Th>
                                    <Th align="right">Status</Th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                {recentTransactions.map((txn, idx) => (
                                    <tr
                                        key={idx}
                                        className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                                    >
                                        <td className="py-3 pr-4 font-mono text-xs font-medium text-gray-800 dark:text-white">
                                            {txn.reference}
                                        </td>
                                        <td className="py-3 px-4">
                                            <TypePill type={txn.type} />
                                        </td>
                                        <td className="py-3 px-4 text-gray-700 dark:text-gray-300 font-semibold tabular-nums">
                                            KES {Number(txn.amount || 0).toLocaleString()}
                                        </td>
                                        <td className="py-3 px-4 text-gray-500 dark:text-slate-400 text-xs">
                                            {txn.date
                                                ? new Date(txn.date).toLocaleDateString('en-KE', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                })
                                                : 'N/A'}
                                        </td>
                                        <td className="py-3 pl-4 text-right">
                                            <StatusPill status={txn.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12 text-gray-400 dark:text-slate-500">
                        <Activity className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                        <p className="text-sm">No transactions found</p>
                        <p className="text-xs mt-1">
                            Activity will appear here once quotes are created.
                        </p>
                        <Link
                            to="/quotes/new"
                            className="inline-flex items-center gap-1.5 mt-4 text-sm text-brand-600 dark:text-green-400 font-semibold hover:underline"
                        >
                            Create a quote
                            <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                )}
            </div>

            {/* ============ QUICK ACTIONS ============ */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:hidden">
                <QuickAction
                    icon={<Printer className="w-5 h-5" />}
                    title="Print Report"
                    description="Generate a printable version"
                    onClick={() => window.print()}
                    color="blue"
                />
                <QuickAction
                    icon={<Download className="w-5 h-5" />}
                    title="Export CSV"
                    description="Download raw data"
                    onClick={() => exportCSV({ stats, chartData: safeChartData })}
                    color="green"
                />
                <QuickAction
                    icon={<LayoutGrid className="w-5 h-5" />}
                    title="Detailed Analytics"
                    description="View full breakdown"
                    onClick={() => setReportType('detailed')}
                    color="amber"
                />
            </div>
        </div>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

function KpiCard({ icon, label, value, prefix = '', suffix = '', change, subtext, sparkline, gradient }) {
    const animated = useCountUp(typeof value === 'number' ? value : 0, {
        decimals: 0,
    });
    const display = typeof value === 'number'
        ? `${prefix}${animated.toLocaleString()}${suffix}`
        : value;

    const hasChange = typeof change === 'number' && change !== 0;
    const isPositive = change >= 0;

    return (
        <div
            className={`relative group overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
        >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-white/5 blur-xl" />

            <div className="relative p-5">
                <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform">
                        {icon}
                    </div>
                    {hasChange && (
                        <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${isPositive
                                    ? 'bg-green-400/30 text-green-100'
                                    : 'bg-red-400/30 text-red-100'
                                }`}
                        >
                            {isPositive ? (
                                <ArrowUpRight className="w-3 h-3" />
                            ) : (
                                <ArrowDownRight className="w-3 h-3" />
                            )}
                            {Math.abs(change)}%
                        </span>
                    )}
                </div>

                <div className="mt-4 space-y-1">
                    <p className="text-xs uppercase tracking-wider opacity-80 font-semibold">{label}</p>
                    <p className="text-3xl font-extrabold tracking-tight tabular-nums">{display}</p>
                    {subtext && <p className="text-xs opacity-75">{subtext}</p>}
                </div>

                {Array.isArray(sparkline) && sparkline.length > 1 && (
                    <div className="mt-4 opacity-80">
                        <MiniSparkline data={sparkline} />
                    </div>
                )}
            </div>
        </div>
    );
}

function MiniSparkline({ data }) {
    const w = 100;
    const h = 20;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const step = w / (data.length - 1);
    const points = data
        .map((v, i) => `${i * step},${h - ((v - min) / range) * h}`)
        .join(' ');
    const areaPoints = `0,${h} ${points} ${w},${h}`;

    return (
        <svg
            width="100%"
            height={h}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="none"
            className="overflow-visible"
        >
            <defs>
                <linearGradient id="reportSpark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.5)" />
                    <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                </linearGradient>
            </defs>
            <polygon fill="url(#reportSpark)" points={areaPoints} />
            <polyline
                fill="none"
                stroke="rgba(255,255,255,0.9)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={points}
            />
        </svg>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th
            className={`text-${align} py-3 ${align === 'right' ? 'pl-4' : 'pr-4'
                } font-semibold text-gray-400 dark:text-slate-400 text-[10px] uppercase tracking-widest`}
        >
            {children}
        </th>
    );
}

function TypePill({ type }) {
    const styles = {
        Policy: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
        Quote: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    };
    return (
        <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[type] ||
                'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                }`}
        >
            {type}
        </span>
    );
}

function StatusPill({ status }) {
    const s = String(status || '').toLowerCase();
    const styles =
        s === 'paid' || s === 'approved'
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            : s === 'pending' || s === 'submitted'
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                : s === 'draft'
                    ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending';
    return (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${styles}`}>
            {label}
        </span>
    );
}

function LegendDot({ color, label }) {
    return (
        <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-xs text-gray-600 dark:text-slate-400 font-medium">{label}</span>
        </div>
    );
}

function EmptyChart({ message, compact }) {
    return (
        <div
            className={`flex flex-col items-center justify-center ${compact ? 'h-56' : 'h-80'
                } text-center`}
        >
            <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center mb-3">
                <BarChart3 className="w-6 h-6 text-gray-300 dark:text-slate-500" />
            </div>
            <p className="text-sm text-gray-400 dark:text-slate-500 max-w-xs">{message}</p>
        </div>
    );
}

function QuickAction({ icon, title, description, onClick, color }) {
    const colors = {
        blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30',
        green:
            'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
        amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/30',
    };
    const iconColors = {
        blue: 'text-blue-600 dark:text-blue-400',
        green: 'text-emerald-600 dark:text-emerald-400',
        amber: 'text-amber-600 dark:text-amber-400',
    };
    return (
        <button
            onClick={onClick}
            className={`p-5 rounded-2xl border ${colors[color]} hover:shadow-lg transition-all duration-200 text-left group focus:outline-none focus:ring-2 focus:ring-brand-500/40`}
        >
            <div className="flex items-center gap-3">
                <div
                    className={`w-10 h-10 rounded-xl bg-white dark:bg-slate-800 flex items-center justify-center ${iconColors[color]} shadow-sm`}
                >
                    {icon}
                </div>
                <div className="flex-1">
                    <p className="font-semibold text-gray-800 dark:text-white">{title}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400">{description}</p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
        </button>
    );
}

function SkeletonLoader() {
    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8 animate-pulse">
            <div className="h-48 bg-gray-200 dark:bg-slate-700 rounded-3xl" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-36 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
                <div className="h-96 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
            </div>
            <div className="h-64 bg-gray-100 dark:bg-slate-700 rounded-2xl" />
        </div>
    );
}

// =========================================================
// EXPORT CSV
// =========================================================
function exportCSV({ stats, chartData }) {
    const rows = [];

    rows.push(['MAYFAIR INSURANCE — REPORTS EXPORT']);
    rows.push(['Generated', new Date().toLocaleString('en-KE')]);
    rows.push([]);
    rows.push(['SUMMARY']);
    rows.push(['Total Quotes', stats.totalQuotes || 0]);
    rows.push(['Active Policies', stats.activePolicies || 0]);
    rows.push(['Commissions (KES)', stats.totalCommissions || 0]);
    rows.push(['Conversion Rate (%)', stats.conversionRate || 0]);
    rows.push(['Growth Rate (%)', stats.growthRate || 0]);
    rows.push([]);
    rows.push(['MONTHLY BREAKDOWN']);
    rows.push(['Month', 'Quotes', 'Policies', 'Commissions (KES)']);
    (chartData || []).forEach((row) => {
        rows.push([
            row.month || '',
            row.quotes || 0,
            row.policies || 0,
            row.commissions || 0,
        ]);
    });

    const csv = rows
        .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
        .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Mayfair-Reports-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}